import { css, html, LitElement, PropertyValues } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { property } from "lit/decorators/property.js";
import { styleMap } from "lit/directives/style-map.js";
import { state } from "lit/decorators/state.js";
import { AsyncController } from "../../shared/controllers/async-controller";
import { DEFAULT_BASE_URL, SELECTION_METHODS, SUPPORTED_LANGUAGES, WIDGET_NAMES } from "../../api-sdk/constants";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { _loading } from "../../shared/fragments/loading-template";
import { _errorMessage } from "../../shared/fragments/error-message-template";
import { defineCustomText, useCustomText } from "../../shared/fragments/custom-text";
import { cssReset } from "../../shared/css-reset";
import { _cssVariableMap } from "../../shared/fragments/css-variable-map";
import { enforceConfig } from "../../shared/enforce-config";
import { MissingConfig } from "../../utils/beam-errors";
import { TNumericId } from "../../shared/types";
import {
  postChainEligibleNonprofitsForCart,
  postFindOrCreateSubscription,
  updateSubscription,
} from "../../api-sdk/v3/routes";
import { LANGUAGES } from "../../api-sdk/types";
import { BeamNonprofitSelectEvent } from "../../utils/events";
import { progressBarConfigDefaults } from "../../shared/components/progress-bar";
import { cssResponsiveUtils } from "../../shared/responsive";
import { makeApiKeyHeader } from "../../utils/makeApiKeyHeader";
import { compileUserString, renderUserString } from "../../shared/fragments/user-strings";
import { strings } from "./strings";

interface RequiredDataPropsWithEmail {
  email: string;
  apiKey: string;
  subscriptionId: string;
}

interface RequiredDataPropsWithPartnerUserId {
  partnerUserId: string;
  apiKey: string;
  subscriptionId: string;
}

interface RequiredConfig {
  apiKey: string;
  subscriptionId: string;
}

export class BeamSubscriberSelectNonprofit extends LitElement {
  static tagName = "beam-subscriber-select-nonprofit";

  @property({ type: String }) public baseUrl: string = DEFAULT_BASE_URL;
  @property({ type: String }) public apiKey!: string;
  @property({ type: String }) public subscriptionId!: string;
  @property({ type: Number, reflect: true }) public storeId?: number;
  @property({ type: String }) public email?: RequiredDataPropsWithEmail["email"];
  @property({ type: String }) public partnerUserId?: RequiredDataPropsWithPartnerUserId["partnerUserId"];
  @property({ type: String }) public postalCode?: string;
  @property({ type: String }) public countryCode?: string;
  @property({ type: Number, reflect: true }) public selectedNonprofitId?: TNumericId;
  @property({ type: Number }) public preselectNonprofitAtIndex?: number;
  @property({ type: String }) public lang: LANGUAGES = "en";
  @property({ type: Boolean }) public debug = false;
  @property({ type: Boolean }) public draftConfig = false;
  @property({ type: Object }) public templateData: Record<string, any> = {};
  @state() private chain: Record<string, any> = {};

  // Resolves once the subscription find/create lookup (if any) has settled, so nonprofit
  // list handling can tell whether the subscriber already has a saved selection
  private subscriptionLookup: Promise<unknown> = Promise.resolve();

  get configLang() {
    return SUPPORTED_LANGUAGES[this.lang] || "en";
  }

  private getChainNonprofits = async () => {
    if (!enforceConfig<RequiredConfig>(["apiKey", "subscriptionId"], this)) {
      throw new MissingConfig();
    }

    const res = await postChainEligibleNonprofitsForCart({
      baseUrl: this.baseUrl,
      headers: makeApiKeyHeader(this.apiKey),
      requestBody: {
        storeId: this.storeId,
        widgetName: WIDGET_NAMES.subscriber_select_nonprofit,
        postalCode: this.postalCode,
        countryCode: this.countryCode,
        version: "1.0.0",
        lang: this.configLang,
        options: {
          config: {
            draftConfig: this.draftConfig,
          },
        },
      },
    });

    if (res.store?.id && res.store.id !== this.storeId) {
      this.storeId = res.store.id;
    }

    // Wait for the subscription to be found/created (if applicable) so we know whether
    // it already has a saved nonprofit selection before we clear or preselect one
    await this.subscriptionLookup;

    // Clear selection if the saved nonprofit is no longer in the eligible list
    if (
      this.selectedNonprofitId != null &&
      !res.nonprofits.map((np) => np.nonprofit.id).includes(this.selectedNonprofitId)
    ) {
      this.selectedNonprofitId = undefined;
    }

    // Auto-select the nonprofit at the given 1-based position if there is no existing selection
    if (this.selectedNonprofitId == null && this.preselectNonprofitAtIndex != null) {
      const preselectedNonprofit = res.nonprofits[this.preselectNonprofitAtIndex - 1];
      if (preselectedNonprofit) {
        this.selectedNonprofitId = preselectedNonprofit.nonprofit.id;
        await this.selectionDataController.exec({ selectedNonprofitId: preselectedNonprofit.nonprofit.id });
      }
    }

    this.chain = { ...res?.chain };

    return res;
  };

  private fetchSubscription = async () => {
    if (!enforceConfig<RequiredConfig>(["apiKey", "subscriptionId"], this)) {
      throw new MissingConfig();
    }

    const result = await postFindOrCreateSubscription({
      baseUrl: this.baseUrl,
      headers: makeApiKeyHeader(this.apiKey),
      requestBody: {
        remoteSubscriptionId: this.subscriptionId,
        email: this.email,
        partnerUserId: this.partnerUserId,
      },
    });

    // Pre-populate selection from the subscription record if not already set
    if (result.nonprofit?.id && this.selectedNonprofitId === undefined) {
      this.selectedNonprofitId = result.nonprofit.id;
    }

    return result;
  };

  private updateNonprofitForSubscription = async ({ selectedNonprofitId }: { selectedNonprofitId: TNumericId }) => {
    if (!enforceConfig<RequiredConfig>(["apiKey", "subscriptionId"], this)) {
      throw new MissingConfig();
    }

    const result = await updateSubscription({
      baseUrl: this.baseUrl,
      headers: makeApiKeyHeader(this.apiKey),
      requestBody: {
        remoteSubscriptionId: this.subscriptionId,
        nonprofitId: selectedNonprofitId,
        selectionMethod: SELECTION_METHODS.subscriber_select_nonprofit_widget,
      },
    });

    await this.updateComplete;

    const selectedNonprofit = this.getNonprofitById(selectedNonprofitId);

    this.dispatchEvent(
      new BeamNonprofitSelectEvent({
        selectedNonprofitId,
        nonprofitName: selectedNonprofit?.nonprofit?.name ?? null,
        source: WIDGET_NAMES.subscriber_select_nonprofit,
        timestamp: new Date(),
      }),
    );

    return result;
  };

  private nonprofitListDataController = new AsyncController<typeof this.getChainNonprofits>(
    this,
    this.getChainNonprofits,
  );

  private subscriptionDataController = new AsyncController<typeof this.fetchSubscription>(this, this.fetchSubscription);

  private selectionDataController = new AsyncController<typeof this.updateNonprofitForSubscription>(
    this,
    this.updateNonprofitForSubscription,
  );

  private getNonprofitById(id: number | null) {
    if (!id) return null;
    return this.nonprofitListDataController?.data?.nonprofits.find((np) => np.nonprofit.id === id);
  }

  async connectedCallback() {
    super.connectedCallback();
    this.nonprofitListDataController.loading = true;
  }

  async firstUpdated() {
    if (!this.apiKey || (!this.email && !this.partnerUserId)) return;
    this.subscriptionLookup = this.subscriptionDataController.exec();
    await this.subscriptionLookup;
  }

  async updated(changedProperties: PropertyValues) {
    const requireNewDataProps = ["baseUrl", "storeId", "apiKey", "countryCode", "postalCode", "lang"];
    for (const prop of requireNewDataProps) {
      if (changedProperties.has(prop)) {
        await this.nonprofitListDataController.exec();
        break;
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  private makeHandleSelect =
    (id: number, index: number, nonprofits: { nonprofit: { id: number } }[]) => async (evt: Event) => {
      const currentId = this.selectedNonprofitId;
      if (evt instanceof KeyboardEvent) {
        let nextFocus = null;
        switch (evt.key) {
          case "ArrowUp":
          case "ArrowLeft":
            nextFocus = index === 0 ? nonprofits[nonprofits.length - 1] : nonprofits[index - 1];
            evt.preventDefault();
            break;
          case "ArrowRight":
          case "ArrowDown":
            nextFocus = index === nonprofits.length - 1 ? nonprofits[0] : nonprofits[index + 1];
            evt.preventDefault();
            break;
          case "Enter":
          case " ":
            evt.preventDefault();
            break;
          default:
            return;
        }
        if (nextFocus) {
          if (currentId != null) {
            this.selectedNonprofitId = nextFocus.nonprofit.id;
          }
          const focusTarget = this.renderRoot.querySelector(`[data-value="${nextFocus.nonprofit.id}"]`) as HTMLElement;
          if (focusTarget !== null) {
            focusTarget.tabIndex = 0;
            focusTarget.focus();
          }
          return;
        }
      }
      const targetEl = evt.currentTarget;
      if (!(targetEl instanceof HTMLElement) || currentId === id) return;
      this.selectedNonprofitId = id;
      await this.selectionDataController.exec({ selectedNonprofitId: id });
    };

  public get cssVariables() {
    const defaults = {
      "--beam-fontFamily": "inherit",
      "--beam-fontStyle": "inherit",
      "--beam-fontSize": "inherit",
      "--beam-textColor": "inherit",
      "--beam-backgroundColor": "inherit",
      ...progressBarConfigDefaults,
      "--beam-SubscriberSelectNonprofit-title-textAlign": "inherit",
      "--beam-SubscriberSelectNonprofit-description-textAlign": "inherit",
      "--beam-SubscriberSelectNonprofit-maxWidth": "800px",
      "--beam-SubscriberSelectNonprofit-options-marginTop": "0px",
      "--beam-SubscriberSelectNonprofit-options-iconHeight": "20px",
      "--beam-SubscriberSelectNonprofit-options-padding": "6px 4px",
      "--beam-SubscriberSelectNonprofit-options-borderRadius": "4px",
      "--beam-SubscriberSelectNonprofit-options-borderColor": "currentColor",
      "--beam-SubscriberSelectNonprofit-options-borderWidth": "1px",
      "--beam-SubscriberSelectNonprofit-options--selected-borderColor": "currentColor",
      "--beam-SubscriberSelectNonprofit-options-backgroundColor": "transparent",
      "--beam-SubscriberSelectNonprofit-options-gap": "8px",
      "--beam-SubscriberSelectNonprofit-options-margin": "14px 0 0 0",
      "--beam-SubscriberSelectNonprofit-width": "100%",
      "--beam-SubscriberSelectNonprofit-options--selected-backgroundColor": "currentColor",
      "--beam-SubscriberSelectNonprofit-details-marginTop": "6px",
      "--beam-SubscriberSelectNonprofit-details-borderRadius": "4px",
      "--beam-SubscriberSelectNonprofit-details-borderWidth": "1px",
      "--beam-SubscriberSelectNonprofit-details-borderColor": "currentColor",
      "--beam-SubscriberSelectNonprofit-details-backgroundColor": "inherit",
      "--beam-SubscriberSelectNonprofit-details-padding": "10px",
      ...defineCustomText("--beam-SubscriberSelectNonprofit-title", {
        fontSize: "1em",
        fontWeight: "bold",
      }),
      ...defineCustomText("--beam-SubscriberSelectNonprofit-description", {
        marginTop: "1em",
        fontSize: ".85em",
      }),
      "--beam-SubscriberSelectNonprofit-title-block-margin-right": "8px",
      ...defineCustomText("--beam-SubscriberSelectNonprofit-details-cause", {
        fontSize: "0.85em",
        fontWeight: "bold",
        textTransform: "uppercase",
      }),
      ...defineCustomText("--beam-SubscriberSelectNonprofit-details-beamAttribution", {
        fontSize: "0.85em",
      }),
      ...defineCustomText("--beam-SubscriberSelectNonprofit-details-impactDescription", {
        fontSize: ".85em",
        marginTop: "10px",
        lineHeight: "normal",
      }),
      "--beam-SubscriberSelectNonprofit-details-nonprofitName-fontWeight": "bold",
      "--beam-SubscriberSelectNonprofit-details-nonprofitName-fontStyle": "inherit",
      "--beam-SubscriberSelectNonprofit-details-fundingProgress-marginTop": "10px",
      ...defineCustomText("--beam-SubscriberSelectNonprofit-details-fundingProgressLabel", {
        fontSize: "0.85em",
      }),
    };

    const remoteConfig = this.nonprofitListDataController?.data?.config?.web?.theme || {};

    const config = { ...defaults, ...remoteConfig };

    const serializable = Object.create({
      toCSS() {
        return _cssVariableMap(this as Record<string, string>);
      },
    });

    return Object.assign(serializable, config);
  }

  static styles = [
    cssReset,
    cssResponsiveUtils,
    css`
      :host {
        display: block;
        width: var(--beam-SubscriberSelectNonprofit-width, 100%);
        max-width: var(--beam-SubscriberSelectNonprofit-maxWidth, 800px);
        font-family: var(--beam-fontFamily);
        font-style: var(--beam-fontStyle);
        font-size: var(--beam-fontSize);
        background-color: var(--beam-backgroundColor);
        color: var(--beam-textColor);
        word-break: normal;
      }

      .details-impactDescription {
        ${useCustomText("--beam-SubscriberSelectNonprofit-details-impactDescription")}
      }

      .details-impactDescription .nonprofitName {
        font-weight: var(--beam-SubscriberSelectNonprofit-details-nonprofitName-fontWeight);
        font-style: var(--beam-SubscriberSelectNonprofit-details-nonprofitName-fontStyle, inherit);
      }

      .title-block {
        margin-right: var(--beam-SubscriberSelectNonprofit-title-block-margin-right);
        ${useCustomText("--beam-SubscriberSelectNonprofit-title")}
        text-align: var(--beam-SubscriberSelectNonprofit-title-textAlign);
      }

      .description {
        ${useCustomText("--beam-SubscriberSelectNonprofit-description")}
        text-align: var(--beam-SubscriberSelectNonprofit-description-textAlign);
      }

      .option {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    `,
  ];

  protected render() {
    if (!this.apiKey || (!this.email && !this.partnerUserId)) {
      return html``;
    }

    const { selectedNonprofitId } = this;
    const { data, loading } = this.nonprofitListDataController;

    if (loading && !data) {
      return _loading();
    }
    if (this.nonprofitListDataController.error) {
      if (this.debug) {
        return _errorMessage({ error: this.nonprofitListDataController.error });
      }
      return "";
    }
    if (this.selectionDataController.error && this.debug) {
      return _errorMessage({ error: this.selectionDataController.error });
    }

    const nonprofits = data?.nonprofits || [];
    const selectedNonprofit = nonprofits.find((np) => np.nonprofit.id === selectedNonprofitId) || null;

    const templateContext: Record<string, any> = {
      custom: { ...this.templateData },
      chain: { ...this.chain },
      selectedNonprofit: { ...selectedNonprofit?.nonprofit },
      lang: this.configLang,
    };

    return html`
      <style>
        :host {
          ${this.cssVariables.toCSS()}
        }
      </style>
      <div part="heading">
        <h3 class="title-block" part="title" id="beam-SubscriberSelectNonprofit-title">
          ${renderUserString(data?.config?.web?.title || "", templateContext, strings[this.configLang].ctaTitle())}
        </h3>
        <p class="description" part="description">
          ${renderUserString(
            data?.config?.web?.description || "",
            templateContext,
            strings[this.configLang].ctaMessage(),
          )}
        </p>
      </div>
      <div
        class="options"
        part="options"
        role="radiogroup"
        aria-labelledby="beam-SubscriberSelectNonprofit-title"
        style="display: flex; gap: var(--beam-SubscriberSelectNonprofit-options-gap); margin: var(--beam-SubscriberSelectNonprofit-options-margin, 12px 0 0 0);"
      >
        ${repeat(
          nonprofits,
          (i) => i.nonprofit.id,
          ({ nonprofit }, index) => {
            const isSelected = selectedNonprofitId === nonprofit.id;
            const isFocusable = isSelected || (selectedNonprofit == null && index === 0);
            const optionTemplateContext: Record<string, any> = {
              ...templateContext,
              nonprofit,
            };

            return html`
              <div
                class="option"
                part="option"
                role="radio"
                tabindex="${isFocusable ? 0 : -1}"
                data-value=${nonprofit.id}
                aria-checked=${isSelected}
                @click=${this.makeHandleSelect(nonprofit.id, index, nonprofits)}
                @keydown=${this.makeHandleSelect(nonprofit.id, index, nonprofits)}
                aria-label="${compileUserString(nonprofit.cause || "", optionTemplateContext)}"
                style="${styleMap({
                  cursor: "pointer",
                  flex: "1",
                  textAlign: "center",
                  lineHeight: "1",
                  marginTop: "var(--beam-SubscriberSelectNonprofit-options-marginTop, 0px)",
                  padding: "var(--beam-SubscriberSelectNonprofit-options-padding, 6px 4px)",
                  borderStyle: "solid",
                  position: "relative",
                  borderRadius: "var(--beam-SubscriberSelectNonprofit-options-borderRadius, 4px)",
                  borderColor: isSelected
                    ? nonprofit.causeColor ||
                      "var(--beam-SubscriberSelectNonprofit-options--selected-borderColor, currentColor)"
                    : "var(--beam-SubscriberSelectNonprofit-options-borderColor, currentColor)",
                  borderWidth: "var(--beam-SubscriberSelectNonprofit-options-borderWidth, 1px)",
                  backgroundColor: isSelected
                    ? nonprofit.causeColor ||
                      "var(--beam-SubscriberSelectNonprofit-options--selected-backgroundColor, currentColor)"
                    : "var(--beam-SubscriberSelectNonprofit-options-backgroundColor, transparent)",
                })}"
              >
                <img
                  src="${isSelected ? nonprofit.causeIconSelectedUrl : nonprofit.causeIconUrl}"
                  alt=""
                  role="presentation"
                  data-speedsize-ignore="true"
                  style="
                    height: var(--beam-SubscriberSelectNonprofit-options-iconHeight, 24px);
                    user-select: none;
                    vertical-align: -webkit-baseline-middle;
                  "
                />
              </div>
            `;
          },
        )}
      </div>
      ${selectedNonprofit != null
        ? html`
            <div
              class="details"
              part="details"
              style="
                border: var(--beam-SubscriberSelectNonprofit-details-borderWidth) solid var(--beam-SubscriberSelectNonprofit-details-borderColor);
                border-radius: var(--beam-SubscriberSelectNonprofit-details-borderRadius);
                background-color: var(--beam-SubscriberSelectNonprofit-details-backgroundColor);
                padding: var(--beam-SubscriberSelectNonprofit-details-padding);
                margin-top: var(--beam-SubscriberSelectNonprofit-details-marginTop);
              "
              aria-label="Funding information for selected nonprofit ${selectedNonprofit.nonprofit
                ?.name}. Powered by Beam"
            >
              <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap-reverse">
                <span
                  class="details-cause"
                  style="flex: 0 1; white-space: nowrap; ${useCustomText(
                    "--beam-SubscriberSelectNonprofit-details-cause",
                  )}"
                >
                  ${renderUserString(selectedNonprofit.nonprofit.cause || "", templateContext)}
                </span>
                <div aria-hidden="true">
                  <span
                    class="details-beamAttribution"
                    aria-hidden="true"
                    style="flex: 0 1; white-space: nowrap; ${useCustomText(
                      "--beam-SubscriberSelectNonprofit-details-beamAttribution",
                    )}"
                  >
                    ${strings[this.configLang].beamAttribution()}
                  </span>
                </div>
              </div>
              <p class="details-impactDescription">
                ${renderUserString(selectedNonprofit.impact.description || "", templateContext)}
              </p>
              <div
                style="display: flex; margin-top: var(--beam-SubscriberSelectNonprofit-details-fundingProgress-marginTop); align-items: center;"
              >
                <beam-progress-bar
                  value="${selectedNonprofit.impact.goalProgressPercentage}"
                  style="flex: 1 0;"
                ></beam-progress-bar>
                <span
                  class="details-fundingProgressLabel"
                  style="${useCustomText(
                    "--beam-SubscriberSelectNonprofit-details-fundingProgressLabel",
                  )} white-space: nowrap; text-align: right; flex: 0 1; margin-left: 15px;"
                >
                  ${renderUserString(selectedNonprofit.impact.goalProgressText, templateContext)}
                </span>
              </div>
            </div>
          `
        : ""}
    `;
  }
}

defineCustomElement(BeamSubscriberSelectNonprofit);

declare global {
  interface HTMLElementTagNameMap {
    "beam-subscriber-select-nonprofit": BeamSubscriberSelectNonprofit;
  }
}
